/**
 * @file Streams a level's visibility keys in off the main thread.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY STREAMING AND NOT A BLOCKING BAKE
 * ─────────────────────────────────────────────────────────────────────────────
 * 15 keys across the city is tens of seconds of solving. Paid up front that is
 * tens of seconds of extra loading screen on every load; paid in the background
 * it is invisible, because {@link SunKeyStore#nearestReady} locks the shading to
 * the closest baked direction until the neighbours land. The level ships with
 * ONE key — the one nearest its start hour, solved synchronously so `color3`
 * exists before the first draw (see `SunKeyStore`'s header for why that is not
 * optional) — and the other 14 arrive over the next few seconds.
 *
 * The visible artefact during that window is that the sun's shading STEPS rather
 * than sweeps. Nothing pops, nothing is unlit, and it resolves itself.
 *
 * MESSAGE CONTRACT — see `bake.worker.js`, which implements the other half:
 *   main -> worker  `{type:'sunkeys', id, cfg, occ, dirs,
 *                     jobs:[{index, position, normal}]}`
 *   worker -> main  `{type:'key', id, k, results:[{index, data}]}` (transferred)
 *                   `{type:'done', id, ms}` / `{type:'error', id, message}`
 *
 * One message per KEY, not per key per chunk: a key is only usable once every
 * chunk has it, so 15 messages is the natural granularity and one per chunk per
 * key (tens of thousands) would be pure overhead.
 *
 * @module engine/bake/sunKeysAsync
 */

import { DAY_DEFAULTS, mergeConfig } from './BakeConfig.js';
import { prepareSunKeys } from './sunKeys.js';

/** @typedef {import('./SunKeyStore.js').SunKeyStore} SunKeyStore */
/** @typedef {import('./Occluders.js').OccluderSet} OccluderSet */

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * Copies the vertex arrays a key solve needs off a list of geometries.
 * @param {Array<{geometry: any}>} targets
 * @returns {Array<{index: number, position: Float32Array, normal: Float32Array|null}>}
 */
function toJobs(targets) {
  return targets.map((t, index) => {
    const geo = t.geometry;
    const pos = geo.getAttribute('position');
    const nor = geo.getAttribute('normal');
    // `.slice()` because these are TRANSFERRED — handing the worker a live
    // attribute buffer detaches it and the level renders as a black hole.
    return {
      index,
      position: pos.array.slice(),
      normal: nor ? nor.array.slice() : null,
    };
  });
}

/**
 * Solves ONE key on the calling thread, for every target.
 *
 * Used for the synchronous first key and as the whole fallback path (node,
 * tests, no `Worker`). Callers that want all of them should go through
 * {@link streamSunKeys} instead so the main thread stays responsive.
 *
 * @param {Array<{geometry: any}>} targets
 * @param {{x: number, y: number, z: number}} dir
 * @param {OccluderSet|null} occ
 * @param {any} cfg Merged bake config.
 * @param {Array<any>|null} [solvers] Reusable per-target solvers; pass the array
 *   back on the next call and the dedup is paid once rather than per key.
 * @returns {Uint8Array[]} One per target, in order.
 */
export function solveKeySync(targets, dir, occ, cfg, solvers = null) {
  return targets.map((t, i) => {
    let s = solvers?.[i];
    if (!s) {
      const geo = t.geometry;
      s = prepareSunKeys(geo.getAttribute('position').array, geo.getAttribute('normal')?.array ?? null, cfg);
      if (solvers) solvers[i] = s;
    }
    return s.bakeKey(dir, occ, cfg);
  });
}

/**
 * Fills a store with every key, off the main thread where possible.
 *
 * Resolves when the last key has landed. Callers generally do NOT await it —
 * the level is already playable; awaiting is for tests and for the blocking
 * path.
 *
 * @param {SunKeyStore} store
 * @param {OccluderSet|null} occ
 * @param {Array<{x: number, y: number, z: number}>} dirs From `sunArc.keyDirs()`.
 * @param {Partial<import('../types.js').BakeConfig>|null} cfg
 * @param {{onKey?: (ready: number, total: number) => void, skip?: number[]}} [opts]
 *   `skip` lists keys already solved (the synchronous first key), so the worker
 *   does not redo them.
 * @returns {Promise<{ms: number, worker: boolean, keys: number}>}
 */
export async function streamSunKeys(store, occ, dirs, cfg, opts = {}) {
  const merged = cfg && cfg.__merged ? cfg : mergeConfig(DAY_DEFAULTS, cfg);
  const onKey = opts.onKey ?? null;
  const skip = new Set(opts.skip ?? []);
  const t0 = now();
  const targets = store.targets;

  const wanted = [];
  for (let k = 0; k < dirs.length; k++) if (!skip.has(k)) wanted.push(k);
  if (wanted.length === 0 || targets.length === 0) return { ms: 0, worker: false, keys: 0 };

  const canWorker =
    merged.useWorker &&
    typeof Worker !== 'undefined' &&
    typeof URL !== 'undefined' &&
    typeof document !== 'undefined';

  if (canWorker) {
    try {
      const n = await runKeysInWorker(store, occ, dirs, merged, wanted, onKey);
      return { ms: now() - t0, worker: true, keys: n };
    } catch (err) {
      console.warn('[sunkeys] worker path failed, falling back to the main thread', err);
    }
  }

  // Fallback: same numbers, same bytes, just on this thread. Yields between
  // keys so the frame loop keeps running.
  const solvers = new Array(targets.length).fill(null);
  let done = 0;
  for (const k of wanted) {
    await new Promise((r) => setTimeout(r, 0));
    store.setKey(k, solveKeySync(targets, dirs[k], occ, merged, solvers));
    done++;
    onKey?.(store.ready, dirs.length);
  }
  return { ms: now() - t0, worker: false, keys: done };
}

/**
 * How many workers to shard the chunks across.
 *
 * Measured on the shipped city: 29 keys over ~1.1M unique vertices was ~48 s on
 * ONE worker — the solve is ~1.5 us per unique vertex per key and the city is
 * far bigger than any synthetic stand-in suggests. (The arc ships 15 keys now,
 * so halve that; the shard arithmetic is per key and did not change.) Chunks
 * partition perfectly
 * (each owns its own vertices and its own solver), so this divides almost
 * linearly.
 *
 * `- 1` leaves the main thread a core; the occluder set is CLONED per worker,
 * which is the memory cost of the shard and the reason this is not simply
 * `hardwareConcurrency`.
 *
 * @returns {number}
 */
function shardCount() {
  const hw = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4;
  return Math.max(1, Math.min(8, hw - 1));
}

/**
 * Shards the chunks across several workers, each solving EVERY key for its own
 * slice.
 *
 * ⚠ A key is only usable once every chunk has it — half a key would light some
 * of the city by one sun and the rest by another — so the results are held in
 * `pending` until the last shard reports that key, and only then handed to the
 * store. That is also why `onKey` counts `store.ready` rather than messages.
 *
 * @param {SunKeyStore} store
 * @param {OccluderSet|null} occ
 * @param {Array<{x: number, y: number, z: number}>} dirs
 * @param {any} cfg
 * @param {number[]} wanted
 * @param {((ready: number, total: number) => void)|null} onKey
 * @returns {Promise<number>}
 */
function runKeysInWorker(store, occ, dirs, cfg, wanted, onKey) {
  return new Promise((resolve, reject) => {
    const nTargets = store.targets.length;
    const shards = Math.min(shardCount(), nTargets);
    const allJobs = toJobs(store.targets);
    const occArrays = occ?.toArrays?.() ?? null;
    const id = Math.floor(performance.now()) >>> 0;

    /** @type {Map<number, {arrays: Uint8Array[], left: number}>} */
    const pending = new Map();
    for (const k of wanted) pending.set(k, { arrays: new Array(nTargets), left: shards });

    /** @type {Worker[]} */
    const workers = [];
    let landed = 0;
    let liveShards = shards;
    let settled = false;

    const finish = (fn, arg) => {
      if (settled) return;
      settled = true;
      for (const w of workers) w.terminate();
      fn(arg);
    };

    for (let s = 0; s < shards; s++) {
      // Round-robin rather than contiguous: chunk cost varies hugely across the
      // map (a dense downtown cell against an empty sea cell), and contiguous
      // slices would leave one shard holding the whole city centre.
      const jobs = allJobs.filter((_, i) => i % shards === s);
      const worker = new Worker(new URL('./bake.worker.js', import.meta.url), { type: 'module' });
      workers.push(worker);

      worker.onmessage = (ev) => {
        const msg = ev.data;
        if (!msg || msg.id !== id) return;
        if (msg.type === 'key') {
          const slot = pending.get(msg.k);
          if (!slot) return;
          for (const r of msg.results) slot.arrays[r.index] = r.data;
          if (--slot.left === 0) {
            pending.delete(msg.k);
            store.setKey(msg.k, slot.arrays);
            landed++;
            onKey?.(store.ready, dirs.length);
          }
        } else if (msg.type === 'done') {
          if (--liveShards === 0) finish(resolve, landed);
        } else if (msg.type === 'error') {
          finish(reject, new Error(msg.message));
        }
      };
      worker.onerror = (e) => finish(reject, e instanceof Error ? e : new Error('sunkeys worker error'));

      worker.postMessage(
        {
          type: 'sunkeys',
          id,
          cfg,
          // Structured-cloned per shard, never transferred — every worker needs
          // the WHOLE occluder set to trace a ray out of its own chunk.
          occ: occArrays,
          dirs,
          keys: wanted,
          jobs,
        },
        jobs.flatMap((j) => (j.normal ? [j.position.buffer, j.normal.buffer] : [j.position.buffer])),
      );
    }
  });
}
